import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import { LoadingIndicator } from '@components/loading-indicator';
import { showError } from '@components/app-error';
import { AipMemberCard } from './aip-member-card';
import { OutreachActionDrawer } from './outreach-action-drawer';
import { MemberDetailPanel } from './member-detail-panel';
import { bustCache, cachedFetch } from './dashboard-cache';

const MEMBER_SIGNALS_TTL_MS = 60 * 1000;

type Member = Awaited<
  ReturnType<typeof rpx.engagementRetention.getMemberSignals>
>['members'][number];
type Segments = Awaited<ReturnType<typeof rpx.engagementRetention.getMemberSignals>>['segments'];
type Scoreboard = Awaited<ReturnType<typeof rpx.engagementRetention.getActorScoreboard>>;

export function RetentionTab() {
  const [isLoading, setIsLoading] = useState(true);
  const [segments, setSegments] = useState<Segments | undefined>(undefined);
  const [scoreboard, setScoreboard] = useState<Scoreboard | undefined>(undefined);
  const [activeMember, setActiveMember] = useState<Member | undefined>(undefined);
  const [drawerMember, setDrawerMember] = useState<Member | undefined>(undefined);
  const [drawerKind, setDrawerKind] = useState<'outcome' | 'note' | 'pause' | 'booking'>('outcome');

  async function refresh() {
    setIsLoading(true);
    try {
      const [signals, sb] = await Promise.all([
        cachedFetch(
          'memberSignals',
          () => rpx.engagementRetention.getMemberSignals({}),
          MEMBER_SIGNALS_TTL_MS,
        ),
        rpx.engagementRetention.getActorScoreboard({ sinceDays: 7 }),
      ]);
      setSegments(signals.segments);
      setScoreboard(sb);
    } catch (err) {
      showError(err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  if (isLoading || !segments) {
    return <LoadingIndicator />;
  }

  const hot = segments.hotCancellations;
  const warm = segments.warmSlipping;
  const watch = segments.watchTrialEnding;

  const mrrAtRiskCents = [...hot, ...warm].reduce((sum, m) => sum + (m.mrrCents ?? 0), 0);
  const totalConversations = hot.length + warm.length;

  return (
    <div class="flex flex-col gap-6">
      <ScoreboardStrip
        mrrAtRiskCents={mrrAtRiskCents}
        totalConversations={totalConversations}
        scoreboard={scoreboard}
      />

      <Band
        title="HOT — Cancelled or canceling"
        subtitle="Canceling now or canceled in the last 7 days"
        members={hot}
        emptyText="No fresh cancellations."
        onAction={(m, kind) => {
          setDrawerMember(m);
          setDrawerKind(kind);
        }}
        onOpen={(m) => setActiveMember(m)}
      />

      <Band
        title="WARM — Slipping away"
        subtitle="2+ prior calls, none in last 2 weeks"
        members={warm}
        emptyText="Nobody is silently drifting."
        onAction={(m, kind) => {
          setDrawerMember(m);
          setDrawerKind(kind);
        }}
        onOpen={(m) => setActiveMember(m)}
      />

      {watch.length > 0 && (
        <Band
          title="WATCH — Trial ending soon"
          subtitle="Trial ends in <=7 days, low engagement"
          members={watch}
          emptyText=""
          onAction={(m, kind) => {
            setDrawerMember(m);
            setDrawerKind(kind);
          }}
          onOpen={(m) => setActiveMember(m)}
        />
      )}

      {drawerMember && (
        <OutreachActionDrawer
          member={drawerMember}
          source="ari_card"
          kind={drawerKind}
          onClose={() => setDrawerMember(undefined)}
          onLogged={(opts) => {
            if (!opts?.keepOpen) {
              setDrawerMember(undefined);
            }
            bustCache('memberSignals');
          }}
        />
      )}

      {activeMember && (
        <MemberDetailPanel
          memberUserId={activeMember.userId}
          onClose={() => setActiveMember(undefined)}
        />
      )}
    </div>
  );
}

function ScoreboardStrip(props: {
  mrrAtRiskCents: number;
  totalConversations: number;
  scoreboard: Scoreboard | undefined;
}) {
  const dollars = (cents: number) =>
    `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  const saved = props.scoreboard?.mrrSavedCents ?? 0;
  const total = props.scoreboard?.total ?? 0;
  const savedCount = props.scoreboard?.byOutcome.find((b) => b.outcome === 'saved')?.count ?? 0;
  const pausedCount = props.scoreboard?.byOutcome.find((b) => b.outcome === 'paused')?.count ?? 0;
  const wonBackCount =
    props.scoreboard?.byOutcome.find((b) => b.outcome === 'won_back')?.count ?? 0;

  return (
    <div class="rounded-xl border border-gray-200 bg-white p-5">
      <div class="text-sm font-semibold text-gray-700">Your day</div>
      <div class="mt-1 text-2xl font-semibold text-gray-900">
        {dollars(props.mrrAtRiskCents)} MRR at risk
        {' · '}
        {props.totalConversations}{' '}
        {props.totalConversations === 1 ? 'conversation' : 'conversations'}
      </div>
      <div class="mt-3 text-sm text-gray-600">
        This week: <span class="font-medium text-gray-800">{dollars(saved)} saved</span>
        {' · '}
        <span>{savedCount} saves</span>
        {' · '}
        <span>{pausedCount} paused</span>
        {' · '}
        <span>{wonBackCount} won back</span>
        {' · '}
        <span class="text-gray-500">{total} actions logged</span>
      </div>
    </div>
  );
}

function Band(props: {
  title: string;
  subtitle: string;
  members: Member[];
  emptyText: string;
  onAction: (m: Member, kind: 'outcome' | 'note' | 'pause' | 'booking') => void;
  onOpen: (m: Member) => void;
}) {
  return (
    <section class="rounded-xl border border-gray-200 bg-white p-5">
      <header class="mb-3 flex items-baseline justify-between">
        <h3 class="text-base font-semibold text-gray-900">
          {props.title} ({props.members.length})
        </h3>
        <p class="text-xs text-gray-500">{props.subtitle}</p>
      </header>
      {props.members.length === 0 ? (
        <p class="text-sm text-gray-500">{props.emptyText}</p>
      ) : (
        <div class="flex flex-col gap-3">
          {props.members.map((m) => (
            <AipMemberCard
              key={m.userId}
              member={m}
              onAction={props.onAction}
              onOpen={props.onOpen}
            />
          ))}
        </div>
      )}
    </section>
  );
}
